/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */

import { useNode } from "@craftjs/core";
import {
    FormControl,
    Grid,
    IconButton,
    InputLabel,
    MenuItem,
    RadioGroup,
    Select,
    Slider,
    Typography,
} from "@mui/material";
import { ReactNode } from "react";
import SettingsItemText from "../SettingsItemText";

import ClearIcon from "@mui/icons-material/Clear";

interface SettingsItemProps {
    prefix?: string;
    label?: string;
    fullWidth?: boolean;
    propKey: string;
    index?: number;
    children?: ReactNode;
    type: string;
    onChange?: (value: any) => any;
    [key: string]: any;
    defaultValue?: any;
}

const SettingsItem = ({
    fullWidth: full = false,
    propKey,
    type,
    onChange,
    index,
    clearable = false,
    defaultValue,
    ...props
}: SettingsItemProps) => {
    const {
        actions: { setProp },
        propValue,
    } = useNode((node) => ({ propValue: node.data.props[propKey] }));
    const value = Array.isArray(propValue) ? propValue[index!] : propValue;

    const renderLabel = (): ReactNode => {
        return props.label ? (
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
                {props.label}
            </Typography>
        ) : null;
    };

    const renderSlider = (): ReactNode => {
        return (
            <>
                {renderLabel()}
                <Slider
                    size="small"
                    value={parseInt(value) || 0}
                    onChange={(_, value, __) => {
                        setProp((props: any) => {
                            if (Array.isArray(propValue)) {
                                props[propKey][index!] = onChange ? onChange(value) : value;
                            } else {
                                props[propKey] = onChange ? onChange(value) : value;
                            }
                        }, 1000);
                    }}
                    {...props}
                />
            </>
        );
    };

    const renderRadio = (): ReactNode => {
        return (
            <>
                {renderLabel()}
                <RadioGroup
                    value={value || defaultValue || 0}
                    onChange={(e) => {
                        const value: any = e.target.value;
                        setProp((props: any) => {
                            props[propKey] = onChange ? onChange(value) : value;
                        });
                    }}
                >
                    {props.children}
                </RadioGroup>
            </>
        );
    };

    const renderSelect = (): ReactNode => {
        return (
            <FormControl fullWidth size="small">
                <InputLabel id="label">{renderLabel()}</InputLabel>
                <Select
                    labelId="label"
                    size="small"
                    value={value || ""}
                    onChange={(event) =>
                        setProp(
                            (props: any) =>
                                (props[propKey] = onChange ? onChange(event.target.value) : event.target.value)
                        )
                    }
                    endAdornment={
                        clearable ? (
                            <IconButton
                                sx={{
                                    display: value ? "" : "none",
                                    mr: 1,
                                    color: "text.secondary",
                                    "&:hover": { color: "text.primary" },
                                }}
                                onClick={() =>
                                    setProp(
                                        (props: any) =>
                                            (props[propKey] = onChange ? onChange("event.target.value") : "")
                                    )
                                }
                                disableRipple
                            >
                                <ClearIcon />
                            </IconButton>
                        ) : null
                    }
                    {...props}
                >
                    {clearable && (
                        <MenuItem value="">
                            <em>None</em>
                        </MenuItem>
                    )}
                    {props.children}
                </Select>
            </FormControl>
        );
    };

    const renderTextInput = (): ReactNode => {
        return (
            <SettingsItemText
                type={type}
                value={value}
                onChange={(value) => {
                    setProp((props: any) => {
                        if (Array.isArray(propValue)) {
                            props[propKey][index!] = onChange ? onChange(value) : value;
                        } else {
                            props[propKey] = onChange ? onChange(value) : value;
                        }
                    }, 500);
                }}
                {...props}
            />
        );
    };

    const renderNotImplemented = (): ReactNode => {
        return <Typography variant="caption">Not possible yet to configure `{propKey}`</Typography>;
    };

    const renderInput = (): ReactNode => {
        switch (type) {
            case "background":
            case "color":
            case "number":
            case "text":
                return renderTextInput();
            case "slider":
                return renderSlider();
            case "radio":
                return renderRadio();
            case "select":
                return renderSelect();
            default:
                return renderNotImplemented();
        }
    };

    return <Grid size={{ xs: full ? 12 : 6 }}>{renderInput()}</Grid>;
};

export default SettingsItem;
